/**
 * CrmContracts - Página de gerenciamento de Contratos Publicados
 * 
 * Lista e CRUD de contratos configurados para uso em negócios
 * Permite filtrar por status e workspace
 * Implementa trava de limite de contratos em produção (1 sem módulo, ilimitado com módulo)
 * 
 * RELEASE: v1.1.0 - 2026-02-20
 * - Adicionado cards de totalizadores (Total, Produção, Limite)
 * - Adicionado banner de alerta quando excede limite sem módulo
 * - Adicionado trava no botão Novo Contrato para status production
 */

import React, { useState, useEffect, useRef } from 'react';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { Plus, Search, FileSignature, Pencil, Trash2, MoreVertical, AlertTriangle, ShieldCheck, Infinity, Lock, Crown, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { usePublishedContracts } from '@/hooks/usePublishedContracts';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { PublishedContractFormDialog } from '@/components/contracts/published/PublishedContractFormDialog';
import {
  PUBLISHED_CONTRACT_STATUS_LABELS,
  type PublishedContract,
  type PublishedContractInput,
} from '@/types/contracts';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { logger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';

/**
 * Página principal de gerenciamento de contratos publicados
 */
export default function CrmContracts() {
  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY_CONTRACTS = 'crm_contracts';
  const hasCreatePermission = canCreate(MENU_KEY_CONTRACTS);
  const hasEditPermission = canEdit(MENU_KEY_CONTRACTS);
  const hasDeletePermission = canDelete(MENU_KEY_CONTRACTS);

  // Estado da UI
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterWorkspace, setFilterWorkspace] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setDialogOpen(true));
  const [editingContract, setEditingContract] = useState<PublishedContract | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Dados
  const {
    publishedContracts,
    isLoading,
    createPublishedContract,
    updatePublishedContract,
    deletePublishedContract,
  } = usePublishedContracts();
  const { workspaces } = useCrmWorkspacesHierarchy();
  const { hasDigitalContractsModule, isLoadingLimits } = useResourceLimits();

  // Contagem de contratos em produção via RPC
  const { data: productionCount = 0, refetch: refetchProductionCount } = useQuery({
    queryKey: ['production-contracts-count'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('count_active_published_contracts');
      if (error) {
        logger.error('[CrmContracts]', 'Erro ao contar contratos em produção', { error });
        return 0;
      }
      return data as number;
    },
    staleTime: 30000,
  });

  // Mutation para ajustar automaticamente o limite
  const enforceLimit = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('enforce_contract_production_limit');
      if (error) throw error;
      return data as { action: string; kept_name?: string; inactivated_count?: number };
    },
    onSuccess: (data) => {
      if (data?.action === 'enforced') {
        showToast.success(
          `Limite ajustado: "${data.kept_name}" mantido ativo, ${data.inactivated_count} inativado(s)`
        );
      } else {
        showToast.info('Já está dentro do limite permitido');
      }
      // Recarregar dados
      queryClient.invalidateQueries({ queryKey: ['published-contracts'] });
      refetchProductionCount();
    },
    onError: (error: Error) => {
      showToast.error('Erro ao ajustar limite', error.message);
    },
  });

  // Cálculos de totalizadores
  const totalContracts = publishedContracts.length;
  const maxAllowed = hasDigitalContractsModule ? Infinity : 1;
  const isOverLimit = !hasDigitalContractsModule && productionCount > 1;
  const isAtLimit = !hasDigitalContractsModule && productionCount >= 1;

  // Ref para garantir que o enforcement automático executa apenas 1 vez por sessão
  const hasEnforcedRef = useRef(false);

  // Enforcement automático: detecta excesso e chama RPC sem intervenção manual
  useEffect(() => {
    if (isOverLimit && !isLoadingLimits && !hasEnforcedRef.current) {
      hasEnforcedRef.current = true;
      enforceLimit.mutate();
    }
  }, [isOverLimit, isLoadingLimits]);

  // Filtragem local
  const filtered = publishedContracts.filter((c) => {
    const matchSearch =
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || c.status === filterStatus;
    const matchWorkspace =
      filterWorkspace === 'all' ||
      c.workspace_ids.length === 0 ||
      c.workspace_ids.includes(filterWorkspace);
    return matchSearch && matchStatus && matchWorkspace;
  });

  // Abre dialog de criação
  const handleNew = () => {
    setEditingContract(null);
    setDialogOpen(true);
  };

  // Abre dialog de edição
  const handleEdit = (contract: PublishedContract) => {
    setEditingContract(contract);
    setDialogOpen(true);
  };

  // Fecha dialog
  const handleClose = () => {
    setDialogOpen(false);
    setEditingContract(null);
  };

  // Salva (cria ou atualiza) — aguarda invalidação antes de fechar
  const handleSave = async (input: PublishedContractInput & { id?: string }) => {
    try {
      if (input.id) {
        await updatePublishedContract.mutateAsync(input as PublishedContractInput & { id: string });
      } else {
        await createPublishedContract.mutateAsync(input);
      }
      await queryClient.invalidateQueries({ queryKey: ['published-contracts'] });
      refetchProductionCount();
      handleClose();
    } catch {
      // Erros tratados no hook
    }
  };

  // Confirma exclusão
  const handleConfirmDelete = async () => {
    if (!deletingId) return;
    try {
      await deletePublishedContract.mutateAsync(deletingId);
      refetchProductionCount();
    } finally {
      setDeletingId(null);
    }
  };

  const isSaving =
    createPublishedContract.isPending || updatePublishedContract.isPending;

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto - Padrão unificado CRM */}
      {/* Mobile: título + ações na mesma linha (subtítulo oculto). Desktop: layout original. */}
      <div className="flex flex-col gap-3 p-4 border-b sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center justify-between gap-2 sm:block">
          <div>
            <h1 className="text-xl font-semibold">Contratos</h1>
          </div>
          {/* Ação mobile na mesma linha do título */}
          {hasCreatePermission && (
            <Button size="xs" onClick={handleNew} className="sm:hidden">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {/* Ação desktop */}
        {hasCreatePermission && (
          <Button size="xs" onClick={handleNew} className="hidden sm:inline-flex">
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Novo Contrato
          </Button>
        )}
      </div>


      {/* KPI Cards - Padrão horizontal compacto */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 px-4 py-3">
        {/* Card Total */}
        <Card className="border-0 shadow-none transition-all hover:shadow-md bg-purple-100 dark:bg-purple-900/30">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <FileSignature className="h-5 w-5 shrink-0 text-purple-700 dark:text-purple-300" />
                <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">Total Contratos</p>
              </div>
              <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
                {isLoading ? '-' : totalContracts}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card Produção */}
        <Card className={cn(
          'border-0 shadow-none transition-all hover:shadow-md',
          isOverLimit ? 'bg-pink-100 dark:bg-pink-900/30' : 'bg-lime-100 dark:bg-green-900/30'
        )}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {isOverLimit ? (
                  <AlertTriangle className="h-5 w-5 shrink-0 text-pink-700 dark:text-pink-300" />
                ) : (
                  <ShieldCheck className="h-5 w-5 shrink-0 text-green-700 dark:text-green-300" />
                )}
                <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">Em Produção</p>
              </div>
              <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">
                {isLoadingLimits ? '-' : productionCount}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Card Limite */}
        <Card className="border-0 shadow-none transition-all hover:shadow-md bg-blue-100 dark:bg-blue-900/30">
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {hasDigitalContractsModule ? (
                  <ShieldCheck className="h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300" />
                ) : (
                  <Lock className="h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300" />
                )}
                <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">Limite</p>
              </div>
              {hasDigitalContractsModule ? (
                <div className="flex items-center gap-1 shrink-0">
                  <Infinity className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Ilimitado</span>
                </div>
              ) : (
                <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">1</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Banner promocional - Módulo Contrato Digital */}
      {!hasDigitalContractsModule && productionCount >= 1 && (
        <div className="shrink-0 px-6 py-3 border-b">
          <Card className="relative overflow-hidden border-0 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-amber-950/30">
            {/* Gradient decorativo sutil */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-orange-500/10 to-amber-500/5" />
            
            <CardContent className="relative py-4 px-4 sm:py-5 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              {/* Ícone premium com gradiente e blur */}
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 blur-lg" />
                <div className="relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/20">
                  <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
              </div>
              
              {/* Conteúdo textual */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h3 className="font-semibold text-sm sm:text-base text-amber-700 dark:text-amber-300">
                    Limite atingido
                  </h3>
                  <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px] sm:text-xs">
                    {productionCount}/1
                  </Badge>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  <span className="hidden sm:inline">Contrate o módulo <strong>Contrato Digital</strong> para ativar contratos ilimitados em produção.</span>
                  <span className="sm:hidden">Contrate o módulo Contrato Digital.</span>
                </p>
              </div>
              
              {/* Botões de ação - empilhados em mobile */}
              <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 sm:flex-none border-amber-500/30 text-amber-600 hover:bg-amber-500/10 hover:border-amber-500/50 transition-all"
                  onClick={() => navigate('/tenant/plans')}
                >
                  <Package className="mr-1.5 h-4 w-4" />
                  <span className="hidden sm:inline">Add-on</span>
                  <span className="sm:hidden">Add-on</span>
                </Button>
                <Button 
                  size="sm" 
                  className="flex-1 sm:flex-none bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md shadow-amber-500/20 hover:shadow-lg hover:shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
                  onClick={() => navigate('/tenant/plans')}
                >
                  <Crown className="mr-1.5 h-4 w-4" />
                  <span className="hidden sm:inline">Ver Planos</span>
                  <span className="sm:hidden">Upgrade</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Barra de filtros */}
      <div className="flex items-center gap-3 px-6 py-3 border-b shrink-0">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar contrato..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>

        {/* Filtro status */}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-9 w-36 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            {Object.entries(PUBLISHED_CONTRACT_STATUS_LABELS).map(([value, { label }]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Filtro workspace */}
        <Select value={filterWorkspace} onValueChange={setFilterWorkspace}>
          <SelectTrigger className="h-9 w-40 text-sm">
            <SelectValue placeholder="Workspace" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos workspaces</SelectItem>
            {workspaces.map((ws) => (
              <SelectItem key={ws.id} value={ws.id}>{ws.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileSignature className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm font-medium text-muted-foreground">
              {publishedContracts.length === 0
                ? 'Nenhum contrato cadastrado'
                : 'Nenhum contrato encontrado'}
            </p>
            {publishedContracts.length === 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                Clique em Novo Contrato para começar
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((contract) => {
              const statusInfo = PUBLISHED_CONTRACT_STATUS_LABELS[contract.status];
              const providerName =
                contract.tenant_signature_configs?.signature_providers?.display_name ||
                contract.tenant_signature_configs?.signature_providers?.name;
              const templateName = contract.contract_templates?.name;
              // Calcula label da versão do modelo selecionada
              const versionLabel = (() => {
                // Versão fixada: exibe o snapshot selecionado
                if (contract.template_version_id && contract.contract_template_versions?.version_number != null) {
                  const n = contract.contract_template_versions.version_number;
                  return `v1.${n - 1} (fixada)`;
                }
                // Versão atual: calcula a versão virtual (max snapshot + 1)
                const versions = contract.contract_templates?.contract_template_versions;
                if (versions && versions.length > 0) {
                  const maxN = Math.max(...versions.map((v: { version_number: number }) => v.version_number));
                  return `v1.${maxN} (atual)`;
                }
                return 'v1.0 (atual)';
              })();

              return (
                <div
                  key={contract.id}
                  className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                >
                  {/* Ícone */}
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FileSignature className="h-4 w-4 text-primary" />
                  </div>

                  {/* Informações principais */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{contract.name}</span>
                      <Badge className={cn('text-[10px] h-4 shrink-0', statusInfo.color)}>
                        {statusInfo.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {templateName && (
                        <span className="text-xs text-muted-foreground">
                          Modelo: {templateName} · {versionLabel}
                        </span>
                      )}
                      {providerName && (
                        <span className="text-xs text-muted-foreground">
                          Provider: {providerName}
                        </span>
                      )}
                      {contract.workspace_ids.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {contract.workspace_ids.length} workspace(s)
                        </span>
                      )}
                      {contract.default_signers.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {contract.default_signers.length} signatário(s) padrão
                        </span>
                      )}
                    </div>
                    {contract.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {contract.description}
                      </p>
                    )}
                  </div>

                  {/* Ações */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {hasEditPermission && (
                        <DropdownMenuItem onClick={() => handleEdit(contract)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Editar
                        </DropdownMenuItem>
                      )}
                      {hasDeletePermission && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeletingId(contract.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog criação/edição — passa flags de limite */}
      <PublishedContractFormDialog
        open={dialogOpen}
        onClose={handleClose}
        contract={editingContract}
        onSave={handleSave}
        isSaving={isSaving}
        hasDigitalContractsModule={hasDigitalContractsModule}
        productionCount={productionCount}
      />

      {/* Confirm exclusão */}
      <AlertDialog open={!!deletingId} onOpenChange={(o) => !o && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir contrato?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O contrato será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
